// src/components/Cases/CaseManagerPanel.jsx
import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import {
  Row,
  Col,
  Button,
  Form,
  Spinner,
  Table,
  Badge,
  InputGroup,
  Alert,
  Card,
} from "react-bootstrap";
import ICD10PickerModal from "../SearchICD10/SearchICD10";
import { getCasesByPatientId, createCase, updateCaseById } from "../../acases";
import { getPeopleForUser, createPerson } from "../../apeople";
import { set } from "date-fns";

/**
 * Props
 * - patientId: string (required)
 * - invoiceICDSelections: Array<{ item, isPrimary }>
 * - existingReferrers: Array<{ id, name, bhf, hpcsa }>
 * - currentUserId: string
 * - onUseIcds: (icdSelections[]) => void
 * - variant: "manager" | "picker" (default "manager")
 * - readOnly: boolean (default false) — disables editing (except New if you allow it)
 * - onPickCase: (caseObj) => void — used in "picker" mode when selecting a case
 * - allowCreateInPicker: boolean (default true) — allow creating in picker variant
 * - showOnlyActiveDefault: boolean (default true) — list hides archived (ended) cases by default
 */
export default function CaseManagerPanel({
  patientId,
  invoiceICDSelections = [],
  existingReferrers = [],
  onUseIcds,
  currentUserId,
  variant = "manager",
  readOnly = false,
  onPickCase,
  allowCreateInPicker = true,
  showOnlyActiveDefault = true,
}) {
  // ----------------- helpers -----------------
  const personIdOf = (p) => p?._id?.$oid || p?._id || p?.id || "";

  const normalize = (s) =>
    String(s || "")
      .trim()
      .toLowerCase();

  const findPersonForCase = (caseObj, peopleArr) => {
    const bhf = normalize(caseObj?.referringDoctorBHF);
    const hpcsa = normalize(caseObj?.referringDoctorHPCSA);
    const name = normalize(caseObj?.referringDoctorName);

    // Prefer exact identifier matches
    let match =
      peopleArr.find((p) => normalize(p?.hpcsaNumber) === hpcsa && hpcsa) ||
      peopleArr.find((p) => normalize(p?.bhfNumber) === bhf && bhf);

    if (match) return match;

    // Fallback: name match (less reliable but helps)
    return peopleArr.find((p) => {
      const full = normalize(
        `${p?.title ?? ""} ${p?.firstName ?? ""} ${p?.lastName ?? ""}`
      );
      return full === name;
    });
  };

  const toArray = (x) => {
    if (Array.isArray(x)) return x;
    if (Array.isArray(x?.cases)) return x.cases;
    if (Array.isArray(x?.data)) return x.data;
    if (Array.isArray(x?.items)) return x.items;
    return [];
  };

  const idOf = (c) => c?._id?.$oid || c?._id || c?.id || null;

  const normalizeToMMDDYYYY = (v) => {
    if (!v) return "";
    if (typeof v === "string") {
      if (/^\d{2}-\d{2}-\d{4}$/.test(v)) return v;
      const m = v.match(/^(\d{4})-(\d{2})-(\d{2})/); // ISO
      if (m) return `${m[2]}-${m[3]}-${m[1]}`;
    }
    if (v && typeof v === "object" && v.$date) {
      const iso = String(v.$date);
      const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (m) return `${m[2]}-${m[3]}-${m[1]}`;
    }
    return "";
  };

  const mmddyyyySortKey = (s) => {
    if (!s) return "";
    const [mm, dd, yyyy] = String(s).split("-");
    if (!mm || !dd || !yyyy) return "";
    return `${yyyy}${mm.padStart(2, "0")}${dd.padStart(2, "0")}`;
  };

  const todayKey = () => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}${mm}${dd}`;
  };

  const isActiveCase = (startStr, endStr) => {
    const endKey = mmddyyyySortKey(endStr);
    return !endKey || endKey >= todayKey();
  };

  const mmddyyyyToYyyyMmDd = (s) => {
    if (!s) return "";
    const [mm, dd, yyyy] = String(s).split("-");
    if (!mm || !dd || !yyyy) return "";
    return `${yyyy}-${mm.padStart(2, "0")}-${dd.padStart(2, "0")}`;
  };

  const yyyyMmDdToMmDdYyyy = (s) => {
    if (!s) return "";
    const [yyyy, mm, dd] = String(s).split("-");
    if (!yyyy || !mm || !dd) return "";
    return `${mm.padStart(2, "0")}-${dd.padStart(2, "0")}-${yyyy}`;
  };

  const codeOf = useCallback(
    (obj) => obj?.ICD10_Code || obj?.ICD10_3_Code || "",
    []
  );

  // ----------------- state -----------------
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [cases, setCases] = useState([]);
  const [selectedCaseId, setSelectedCaseId] = useState(null);
  const [mode, setMode] = useState("existing"); // "existing" | "new"
  const [onlyActive, setOnlyActive] = useState(showOnlyActiveDefault);
  const [isEditing, setIsEditing] = useState(false);

  const [useExistingReferrer, setUseExistingReferrer] = useState(true);
  const [selectedReferrerId, setSelectedReferrerId] = useState(
    existingReferrers?.[0]?.id || ""
  );
  const [refName, setRefName] = useState("");
  const [refTitle, setRefTitle] = useState("");
  const [refType, setRefType] = useState("Doctor");
  const [refFirstName, setRefFirstName] = useState("");
  const [refLastName, setRefLastName] = useState("");
  const [refBHF, setRefBHF] = useState("");
  const [refHPCSA, setRefHPCSA] = useState("");

  const [caseDescription, setCaseDescription] = useState("");
  const [authorizedBy, setAuthorizedBy] = useState("");
  const [authorizationNumber, setAuthorizationNumber] = useState("");
  const [caseStartDate, setCaseStartDate] = useState("");
  const [caseEndDate, setCaseEndDate] = useState("");
  const [caseMaxSessions, setCaseMaxSessions] = useState("");
  const [people, setPeople] = useState([]);

  const [showICDModal, setShowICDModal] = useState(false);
  const [icdSelections, setIcdSelections] = useState(() =>
    (invoiceICDSelections || []).map((s) => ({
      item: s.item ?? s,
      isPrimary: !!s.isPrimary,
    }))
  );

  const ROW_ACTIVE = "#CFE2FF";
  const ROW_INACTIVE = "#f8f9fa";
  const newModeInitialized = useRef(false);

  const primaryICDs = useMemo(
    () => icdSelections.filter((s) => s.isPrimary).map((s) => s.item),
    [icdSelections]
  );
  const secondaryICDs = useMemo(
    () => icdSelections.filter((s) => !s.isPrimary).map((s) => s.item),
    [icdSelections]
  );

  const icd101 = useMemo(
    () => codeOf(primaryICDs[0]) || "",
    [primaryICDs, codeOf]
  );
  const icd102 = useMemo(
    () => secondaryICDs.map((i) => codeOf(i)).filter(Boolean),
    [secondaryICDs, codeOf]
  );

  const selectedCase = useMemo(
    () => toArray(cases).find((c) => idOf(c) === selectedCaseId) || null,
    [cases, selectedCaseId]
  );

  // Can user edit right panel?
  const canEdit =
    !readOnly &&
    isEditing &&
    (variant === "manager" || (variant === "picker" && mode === "new"));

  // ----------------- effects -----------------
  useEffect(() => {
    let mounted = true;

    async function loadPeople() {
      try {
        const token = sessionStorage.getItem("user");
        if (!token) return;

        const data = await getPeopleForUser(token);
        if (mounted) setPeople(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error("❌ Failed to load people:", e);
        if (mounted) setPeople([]);
      }
    }

    loadPeople();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    async function load() {
      if (!patientId) return;
      setLoading(true);
      setError("");
      try {
        const data = await getCasesByPatientId(patientId);
        const mapped = toArray(data).map((c) => {
          const startStr = normalizeToMMDDYYYY(c.caseStartDate);
          const endStr = normalizeToMMDDYYYY(c.caseEndDate);
          return {
            ...c,
            _startStr: startStr,
            _endStr: endStr,
            _normId: idOf(c),
          };
        });

        const filtered = onlyActive
          ? mapped.filter((c) => isActiveCase(c._startStr, c._endStr))
          : mapped;

        const arr = filtered
          .slice()
          .sort((a, b) =>
            mmddyyyySortKey(b._startStr).localeCompare(
              mmddyyyySortKey(a._startStr)
            )
          );

        if (mounted) {
          setCases(arr);
          if (arr.length === 0) {
            setSelectedCaseId(null);
            setMode("new");
            setIsEditing(true); // ✅ new case must be editable
          } else {
            // 👇 Only force "existing" if we're NOT currently in "new"
            if (mode !== "new") {
              if (!selectedCaseId) setSelectedCaseId(idOf(arr[0]));
              setMode("existing");
            }
          }
        }
      } catch (e) {
        if (mounted) setError(e?.message || "Failed to load cases");
      } finally {
        if (mounted) setLoading(false);
      }
    }
    load();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, onlyActive]); // keep deps as-is; we rely on the mode guard above

  // Hydrate form when switching to an existing case
  useEffect(() => {
    if (mode !== "existing" || !selectedCase) return;

    // -----------------------
    // 1) Hydrate basic fields
    // -----------------------
    setCaseDescription(selectedCase.caseDescription || "");
    setAuthorizedBy(selectedCase.authorizedBy || "");
    setAuthorizationNumber(selectedCase.authorizationNumber || "");
    setCaseStartDate(normalizeToMMDDYYYY(selectedCase.caseStartDate));
    setCaseEndDate(normalizeToMMDDYYYY(selectedCase.caseEndDate));
    setCaseMaxSessions(selectedCase.caseMaxSessions || "");

    // Always default to "Use existing referrer" when editing an existing case
    setUseExistingReferrer(true);

    // ------------------------------------
    // 2) Hydrate ICDs (selections preferred)
    // ------------------------------------
    if (
      Array.isArray(selectedCase.icdSelections) &&
      selectedCase.icdSelections.length
    ) {
      setIcdSelections(
        selectedCase.icdSelections.map((s) => ({
          item: s.item ?? s,
          isPrimary: !!s.isPrimary,
        }))
      );
    } else {
      const prim = selectedCase.icd101
        ? [{ item: { ICD10_Code: selectedCase.icd101 }, isPrimary: true }]
        : [];
      const secs = toArray(selectedCase.icd102).map((code) => ({
        item: { ICD10_Code: code },
        isPrimary: false,
      }));
      setIcdSelections([...prim, ...secs]);
    }

    // --------------------------------------------
    // 3) Hydrate referrer from People (best effort)
    //    Prefer HPCSA match, then BHF, then name.
    // --------------------------------------------
    const normalize = (v) =>
      String(v || "")
        .trim()
        .toLowerCase();
    const personIdOf = (p) =>
      (p?._id?.$oid || p?._id || p?.id || "").toString();

    const caseHPCSA = normalize(selectedCase.referringDoctorHPCSA);
    const caseBHF = normalize(selectedCase.referringDoctorBHF);
    const caseName = normalize(selectedCase.referringDoctorName);

    const peopleArr = Array.isArray(people) ? people : [];

    let matchedPerson =
      (caseHPCSA
        ? peopleArr.find((p) => normalize(p?.hpcsaNumber) === caseHPCSA)
        : null) ||
      (caseBHF
        ? peopleArr.find((p) => normalize(p?.bhfNumber) === caseBHF)
        : null) ||
      (caseName
        ? peopleArr.find((p) => {
            const full = normalize(
              `${p?.title ?? ""} ${p?.firstName ?? ""} ${p?.lastName ?? ""}`
            );
            return full === caseName;
          })
        : null);

    if (matchedPerson) {
      // Use authoritative People values + select the dropdown option
      setSelectedReferrerId(personIdOf(matchedPerson));
      setRefName(
        `${matchedPerson?.title ?? ""} ${matchedPerson?.firstName ?? ""} ${
          matchedPerson?.lastName ?? ""
        }`.trim()
      );
      setRefBHF(matchedPerson?.bhfNumber || "");
      setRefHPCSA(matchedPerson?.hpcsaNumber || "");
    } else {
      // Fallback: show whatever was saved on the case
      setSelectedReferrerId("");
      setRefName(selectedCase.referringDoctorName || "");
      setRefBHF(selectedCase.referringDoctorBHF || "");
      setRefHPCSA(selectedCase.referringDoctorHPCSA || "");
    }
  }, [mode, selectedCase, people]);

  useEffect(() => {
    if (!useExistingReferrer) {
      setSelectedReferrerId("");
      setRefName("");
      // keep refBHF/refHPCSA if you want user to continue typing
    }
  }, [useExistingReferrer]);

  // Reset fields for a new case
  useEffect(() => {
    // leaving "new" -> allow re-init next time
    if (mode !== "new") {
      newModeInitialized.current = false;
      return;
    }

    // already initialized this entry into "new"
    if (newModeInitialized.current) return;
    newModeInitialized.current = true;

    setSelectedCaseId(null);

    // Only set a default once; if there are referrers, start in "existing referrer" mode,
    // otherwise start in "create new referrer" mode.
    const hasPeople = toArray(people).length > 0;
    setUseExistingReferrer(hasPeople);
    setSelectedReferrerId(
      hasPeople ? people[0]?._id?.$oid || people[0]?._id : ""
    );

    setRefName("");
    setRefBHF("");
    setRefHPCSA("");
    setCaseDescription("");
    setAuthorizedBy("");
    setAuthorizationNumber("");
    setCaseStartDate("");
    setCaseEndDate("");
    setCaseMaxSessions("");

    // Seed ICDs from invoice selections (only once on entry to "new")
    setIcdSelections(
      toArray(invoiceICDSelections).map((s) => ({
        item: s.item ?? s,
        isPrimary: !!s.isPrimary,
      }))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, people]);

  // Keep referrer details synced when choosing an existing referrer
  useEffect(() => {
    if (!useExistingReferrer) return;

    const p = people.find(
      (x) => (x?._id?.$oid || x?._id) === selectedReferrerId
    );

    if (p) {
      setRefName(
        `${p.title ?? ""} ${p.firstName ?? ""} ${p.lastName ?? ""}`.trim()
      );
      setRefBHF(p.bhfNumber || "");
      setRefHPCSA(p.hpcsaNumber || "");
    } else {
      setRefName("");
      setRefBHF("");
      setRefHPCSA("");
    }
  }, [useExistingReferrer, selectedReferrerId, people]);

  // ----------------- actions -----------------
  const startNewCase = () => {
    if (variant === "picker" && !allowCreateInPicker) return;
    setMode("new");
    setIsEditing(true); // ✅ new case must be editable
  };

  const refreshCases = async (selectId) => {
    if (!patientId) return;
    setLoading(true);
    setError("");
    try {
      const data = await getCasesByPatientId(patientId);
      const mapped = toArray(data).map((c) => {
        const startStr = normalizeToMMDDYYYY(c.caseStartDate);
        const endStr = normalizeToMMDDYYYY(c.caseEndDate);
        return { ...c, _startStr: startStr, _endStr: endStr, _normId: idOf(c) };
      });

      const filtered = onlyActive
        ? mapped.filter((c) => isActiveCase(c._startStr, c._endStr))
        : mapped;

      const arr = filtered
        .slice()
        .sort((a, b) =>
          mmddyyyySortKey(b._startStr).localeCompare(
            mmddyyyySortKey(a._startStr)
          )
        );

      setCases(arr);
      if (arr.length === 0) {
        setSelectedCaseId(null);
        setMode(
          variant === "manager" || (variant === "picker" && allowCreateInPicker)
            ? "new"
            : "existing"
        );
      } else if (selectId) {
        setSelectedCaseId(selectId);
        setMode("existing");
      } else if (!selectedCaseId) {
        setSelectedCaseId(idOf(arr[0]));
        setMode("existing");
      }
    } catch (e) {
      setError(e?.message || "Failed to refresh cases");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!canEdit) return;
    if (!patientId) return alert("Patient is required.");
    if (!icd101)
      return alert("Please select at least one primary ICD-10 code.");

    const token = sessionStorage.getItem("user");
    if (!token) return alert("You are not logged in. Please sign in again.");

    try {
      let finalRefName = refName || "";
      let finalBHF = refBHF || "";
      let finalHPCSA = refHPCSA || "";

      // ✅ If creating a new referrer, create People first
      if (!useExistingReferrer) {
        // (Optional but recommended) basic validation
        if (!refFirstName || !refLastName) {
          return alert("Referrer first name and last name are required.");
        }

        const createdPerson = await createPerson(
          {
            type: refType || "GP", // or your selected type value
            title: refTitle || "",
            firstName: refFirstName || "",
            lastName: refLastName || "",
            bhfNumber: finalBHF,
            hpcsaNumber: finalHPCSA,
          },
          token
        );

        // ✅ Use returned values if backend normalizes them
        finalRefName = `${createdPerson?.title ?? refTitle ?? ""} ${
          createdPerson?.firstName ?? refFirstName ?? ""
        } ${createdPerson?.lastName ?? refLastName ?? ""}`.trim();
        finalBHF = createdPerson?.bhfNumber ?? finalBHF;
        finalHPCSA = createdPerson?.hpcsaNumber ?? finalHPCSA;
      }

      const payload = {
        patientId,
        referringDoctorName: useExistingReferrer ? refName || "" : finalRefName,
        referringDoctorBHF: finalBHF || "",
        referringDoctorHPCSA: finalHPCSA || "",
        icd101,
        icd102,
        icdSelections,
        caseDescription: caseDescription || "",
        authorizedBy: authorizedBy || "",
        authorizationNumber: authorizationNumber || "",
        caseStartDate: caseStartDate || "",
        caseEndDate: caseEndDate || "",
        caseMaxSessions: caseMaxSessions || "",
        caseUpdatedBy: currentUserId || "",
      };

      if (mode === "new" || !selectedCase?._id) {
        const created = await createCase({
          ...payload,
          caseCreatedBy: currentUserId || "",
        });
        await refreshCases(created?._id);
      } else {
        await updateCaseById(selectedCase._id, payload);
        await refreshCases(selectedCase._id);
      }
    } catch (e) {
      console.error("Failed to save case:", e);
      alert(e?.message || "Failed to save case");
    }
    setIsEditing(false);
  };

  const handleUseICDs = () => onUseIcds?.(icdSelections);

  const handlePick = () => {
    if (!onPickCase || !selectedCase) return;
    onPickCase(selectedCase);
  };

  // ----------------- UI bits -----------------
  const LeftList = () => (
    <div className="rounded p-2">
      <div className="d-flex justify-content-between align-items-center mb-2">
        {loading ? (
          <span className="d-inline-flex align-items-center gap-2">
            <Spinner animation="border" size="sm" /> <span>Loading</span>
          </span>
        ) : (
          <div className="small text-muted">
            Showing {onlyActive ? "active" : "all"} cases
          </div>
        )}

        <div className="d-flex gap-2">
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => setOnlyActive((v) => !v)}
          >
            {onlyActive ? "Show All" : "Hide Archived"}
          </Button>

          {(variant === "manager" ||
            (variant === "picker" && allowCreateInPicker)) && (
            <Button
              variant="primary"
              size="sm"
              onClick={startNewCase}
              disabled={readOnly}
            >
              + New Case
            </Button>
          )}
        </div>
      </div>

      {!toArray(cases).length && !loading ? (
        <div className="text-muted">No cases found.</div>
      ) : (
        <Table hover size="sm" responsive className="mb-0">
          <tbody className="border">
            {toArray(cases).map((c) => {
              const isActiveRow = idOf(c) === selectedCaseId;
              return (
                <tr
                  key={idOf(c)}
                  onClick={() => {
                    setSelectedCaseId(idOf(c));
                    setMode("existing");
                    setIsEditing(false); // ✅ always open in view mode
                  }}
                  style={{
                    cursor: "pointer",
                    backgroundColor: isActiveRow ? ROW_ACTIVE : ROW_INACTIVE,
                  }}
                >
                  <td
                    style={{
                      backgroundColor: isActiveRow ? ROW_ACTIVE : ROW_INACTIVE,
                    }}
                  >
                    <div className="fw-semibold text-primary">
                      {c?.caseDescription || "Untitled Case"}
                    </div>
                    <div className="small text-muted">
                      {c?._startStr || "-"} {c?._endStr ? `→ ${c._endStr}` : ""}
                    </div>
                    <div className="small text-muted">
                      Ref: {c?.referringDoctorName || "-"} | BHF{" "}
                      {c?.referringDoctorBHF || "-"} | HPCSA{" "}
                      {c?.referringDoctorHPCSA || "-"}
                    </div>
                    <div className="mt-1">
                      {c?.icd101 && (
                        <Badge bg="secondary" className="me-1">
                          {c.icd101}
                        </Badge>
                      )}
                      {toArray(c?.icd102)
                        .slice(0, 2)
                        .map((s, i) => (
                          <Badge
                            key={`${idOf(c)}-sec-${i}`}
                            bg="light"
                            text="dark"
                            className="me-1"
                          >
                            {s}
                          </Badge>
                        ))}
                      {toArray(c?.icd102).length > 2 && (
                        <span className="text-muted">
                          +{toArray(c?.icd102).length - 2}
                        </span>
                      )}
                    </div>
                  </td>
                  <td
                    className="text-end"
                    style={{
                      backgroundColor: isActiveRow ? ROW_ACTIVE : ROW_INACTIVE,
                    }}
                  >
                    <Button
                      size="sm"
                      variant=""
                      onClick={(e) => {
                        e.stopPropagation(); // ✅ don't trigger row select twice
                        setSelectedCaseId(idOf(c));
                        setMode("existing");
                        setIsEditing(true); // ✅ now edit mode
                      }}
                      disabled={readOnly}
                    >
                      <i className="fa fa-edit text-primary ms-2" />
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}
    </div>
  );

  const ReadOnlyView = () => (
    <Card className="border rounded">
      <Card.Body>
        <div className="d-flex justify-content-between align-items-start">
          <div>
            <h6 className="text-primary mb-1">
              {selectedCase?.caseDescription?.trim() || "Untitled Case"}
            </h6>
            <div className="text-muted small mb-2">
              {selectedCase?._startStr || "—"}
              {selectedCase?._endStr ? ` → ${selectedCase._endStr}` : ""}
            </div>
          </div>

          {variant === "picker" && (
            <div className="text-end">
              <Button
                size="sm"
                variant="outline-primary"
                onClick={handlePick}
                disabled={!selectedCase}
              >
                Use This Case
              </Button>
            </div>
          )}
        </div>

        <hr />

        <Row className="mb-2">
          <Col md={6}>
            <div className="small text-muted">Referring Doctor</div>
            <div>{selectedCase?.referringDoctorName || "—"}</div>
            <div className="small text-muted">
              BHF: {selectedCase?.referringDoctorBHF || "—"} &nbsp;|&nbsp;
              HPCSA: {selectedCase?.referringDoctorHPCSA || "—"}
            </div>
          </Col>
          <Col md={6}>
            <div className="small text-muted">Authorization</div>
            <div>
              {selectedCase?.authorizedBy || "—"}
              {selectedCase?.authorizationNumber
                ? ` (${selectedCase.authorizationNumber})`
                : ""}
            </div>
          </Col>
        </Row>

        <div className="mb-2">
          <div className="small text-muted mb-1">ICD-10</div>
          {selectedCase?.icd101 && (
            <Badge bg="secondary" className="me-1">
              {selectedCase.icd101}
            </Badge>
          )}
          {toArray(selectedCase?.icd102).map((code, i) => (
            <Badge key={`sel-sec-${i}`} bg="light" text="dark" className="me-1">
              {code}
            </Badge>
          ))}
        </div>
      </Card.Body>
    </Card>
  );

  // ----------------- render -----------------
  return (
    <div className="p-2">
      <Row className="align-items-center mb-2">
        <Col>
          <h6 className="text-primary mt-1 fw-normal">PATIENT CASES</h6>
        </Col>
      </Row>

      {error && (
        <Row className="mb-2">
          <Col>
            <Alert variant="danger" className="py-2 mb-0">
              {error}
            </Alert>
          </Col>
        </Row>
      )}

      <Row className="g-3">
        <Col md={4}>
          <LeftList />
        </Col>

        <Col md={8}>
          {/* Right side: either read-only or editable form */}
          {mode === "existing" && selectedCase && !isEditing ? (
            <ReadOnlyView />
          ) : (
            <div className="border rounded p-3">
              <h6 className="text-primary mb-3">
                {mode === "existing"
                  ? canEdit
                    ? "Edit Case"
                    : "View Case"
                  : "New Case"}
              </h6>

              <Row className="mb-2">
                <Col>
                  <div className="d-flex gap-4">
                    <Form.Check
                      type="radio"
                      id="ref-existing"
                      name="referrerMode"
                      label="Use existing referrer"
                      checked={useExistingReferrer === true}
                      onChange={() => setUseExistingReferrer(true)}
                      disabled={!canEdit}
                    />

                    <Form.Check
                      type="radio"
                      id="ref-new"
                      name="referrerMode"
                      label="Create new referrer"
                      checked={useExistingReferrer === false}
                      onChange={() => setUseExistingReferrer(false)}
                      disabled={!canEdit}
                    />
                  </div>
                </Col>
              </Row>

              {useExistingReferrer ? (
                <>
                  <Form.Group className="mb-2">
                    <Form.Label>Referring Doctor</Form.Label>
                    <Form.Select
                      value={selectedReferrerId}
                      onChange={(e) => setSelectedReferrerId(e.target.value)}
                      disabled={!canEdit}
                    >
                      <option value="">Select referrer...</option>
                      {people.map((p) => {
                        const id = personIdOf(p);
                        const label = `${p?.title ?? ""} ${
                          p?.firstName ?? ""
                        } ${p?.lastName ?? ""}`.trim();
                        const typeLabel = p?.type ? ` (${p.type})` : "";
                        return (
                          <option key={id} value={id}>
                            {label}
                            {typeLabel}
                          </option>
                        );
                      })}
                    </Form.Select>
                  </Form.Group>

                  <Row className="mb-2">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>BHF</Form.Label>
                        <Form.Control value={refBHF} readOnly />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>HPCSA</Form.Label>
                        <Form.Control value={refHPCSA} readOnly />
                      </Form.Group>
                    </Col>
                  </Row>
                </>
              ) : (
                <>
                  
  {/* ---------- Row 1: Type, Title, First Name, Last Name ---------- */}
  <Row className="mb-2">
    <Col md={4}>
      <Form.Group controlId="refType">
        <Form.Label>Type</Form.Label>
        <Form.Select
          value={refType}
          onChange={(e) => setRefType(e.target.value)}
          disabled={!canEdit}
        >
          <optgroup label="Medical Practitioners">
            <option value="GP">General Practitioner (GP)</option>
            <option value="Cardio">Cardiologist</option>
            <option value="Derm">Dermatologist</option>
            <option value="Endo">Endocrinologist</option>
            <option value="Gastro">Gastroenterologist</option>
            <option value="Neuro">Neurologist</option>
            <option value="Onco">Oncologist</option>
            <option value="Ortho">Orthopaedic Surgeon</option>
            <option value="Pead">Paediatrician</option>
            <option value="Phys">Physician</option>
            <option value="Psych">Psychiatrist</option>
            <option value="Pulmo">Pulmonologist</option>
          </optgroup>

          <optgroup label="Surgical & Diagnostic Specialists">
            <option value="ENT">ENT Specialist</option>
            <option value="Eye">Ophthalmologist</option>
            <option value="Radio">Radiologist</option>
            <option value="Patho">Pathologist</option>
          </optgroup>

          <optgroup label="Allied Health Professionals">
            <option value="Physio">Physiotherapist</option>
            <option value="OT">Occupational Therapist</option>
            <option value="Biokineticist">Biokineticist</option>
            <option value="Dietician">Dietitian</option>
            <option value="Speech">Speech Therapist</option>
            <option value="Audio">Audiologist</option>
            <option value="Psychologist">Clinical Psychologist</option>
            <option value="Chiro">Chiropractor</option>
            <option value="Podiatrist">Podiatrist</option>
          </optgroup>

          <optgroup label="Nursing & Pharmacy">
            <option value="Nurse">Nurse</option>
            <option value="Pharma">Pharmacist</option>
          </optgroup>

          <optgroup label="Dental">
            <option value="Dentist">Dentist</option>
          </optgroup>

          <option value="Other">Other</option>
        </Form.Select>
      </Form.Group>
    </Col>

    <Col md={2}>
      <Form.Group>
        <Form.Label>Title</Form.Label>
        <Form.Select
          value={refTitle}
          onChange={(e) => setRefTitle(e.target.value)}
          disabled={!canEdit}
        >
          <option value="">-</option>
          <option value="Dr">Dr</option>
          <option value="Mr">Mr</option>
          <option value="Mrs">Mrs</option>
          <option value="Ms">Ms</option>
          <option value="Prof">Prof</option>
        </Form.Select>
      </Form.Group>
    </Col>

    <Col md={3}>
      <Form.Group>
        <Form.Label>First Name</Form.Label>
        <Form.Control
          value={refFirstName}
          onChange={(e) => setRefFirstName(e.target.value)}
          disabled={!canEdit}
        />
      </Form.Group>
    </Col>

    <Col md={3}>
      <Form.Group>
        <Form.Label>Last Name</Form.Label>
        <Form.Control
          value={refLastName}
          onChange={(e) => setRefLastName(e.target.value)}
          disabled={!canEdit}
        />
      </Form.Group>
    </Col>
  </Row>

  {/* ---------- Row 2: BHF, HPCSA ---------- */}
  <Row className="mb-2">
    <Col md={6}>
      <Form.Group>
        <Form.Label>BHF</Form.Label>
        <Form.Control
          value={refBHF}
          onChange={(e) => setRefBHF(e.target.value)}
          placeholder="e.g. 0123456"
          disabled={!canEdit}
        />
      </Form.Group>
    </Col>

    <Col md={6}>
      <Form.Group>
        <Form.Label>HPCSA</Form.Label>
        <Form.Control
          value={refHPCSA}
          onChange={(e) => setRefHPCSA(e.target.value)}
          placeholder="e.g. MP0123456"
          disabled={!canEdit}
        />
      </Form.Group>
    </Col>
  </Row>
</>

              )}

              <Form.Group className="mb-2">
                <Form.Label>Case Description</Form.Label>
                <Form.Control
                  value={caseDescription}
                  onChange={(e) => setCaseDescription(e.target.value)}
                  placeholder="Brief description of the case"
                  disabled={!canEdit}
                />
              </Form.Group>

              <Row className="mb-2">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Authorized By</Form.Label>
                    <Form.Control
                      value={authorizedBy}
                      onChange={(e) => setAuthorizedBy(e.target.value)}
                      placeholder="Name of authorizer"
                      disabled={!canEdit}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Authorization Number</Form.Label>
                    <Form.Control
                      value={authorizationNumber}
                      onChange={(e) => setAuthorizationNumber(e.target.value)}
                      placeholder="Auth number"
                      disabled={!canEdit}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-2">
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Start Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={mmddyyyyToYyyyMmDd(caseStartDate)}
                      onChange={(e) =>
                        setCaseStartDate(yyyyMmDdToMmDdYyyy(e.target.value))
                      }
                      disabled={!canEdit}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>End Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={mmddyyyyToYyyyMmDd(caseEndDate)}
                      onChange={(e) =>
                        setCaseEndDate(yyyyMmDdToMmDdYyyy(e.target.value))
                      }
                      disabled={!canEdit}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Max Sessions</Form.Label>
                    <InputGroup>
                      <Form.Control
                        type="number"
                        min={0}
                        step={1}
                        value={caseMaxSessions}
                        onChange={(e) => setCaseMaxSessions(e.target.value)}
                        disabled={!canEdit}
                      />
                    </InputGroup>
                  </Form.Group>
                </Col>
              </Row>

              <div className="d-flex justify-content-between align-items-center mt-3 mb-2">
                <h6 className="m-0">ICD-10 Codes</h6>
                <div className="d-flex gap-2">
                  <Button
                    variant="outline-secondary"
                    onClick={() => setIcdSelections([])}
                    disabled={!canEdit || !icdSelections.length}
                  >
                    Clear
                  </Button>
                  <Button
                    variant="primary"
                    onClick={() => setShowICDModal(true)}
                    disabled={!canEdit}
                  >
                    Add / Edit ICD-10
                  </Button>
                </div>
              </div>
              <div className="border rounded p-2">
                {(() => {
                  if (!toArray(icdSelections).length) {
                    return (
                      <div className="text-muted">
                        No ICD-10 codes selected.
                      </div>
                    );
                  }
                  const prim = primaryICDs.map(codeOf).filter(Boolean);
                  const sec = icd102;
                  return (
                    <>
                      <div className="mb-1">
                        <strong>Primary</strong>:{" "}
                        {prim.length ? prim.join(", ") : "-"}
                      </div>
                      <div>
                        <strong>Secondary</strong>:{" "}
                        {sec.length ? sec.join(", ") : "-"}
                      </div>
                    </>
                  );
                })()}
              </div>

              <div className="d-flex justify-content-end gap-2 mt-3">
                {mode === "existing" &&
                  selectedCase &&
                  variant !== "picker" && (
                    <Button
                      variant="outline-primary"
                      onClick={handleUseICDs}
                      disabled={!canEdit}
                    >
                      Use ICDs in Invoice
                    </Button>
                  )}

                {/* In picker mode, show a "Use" button even on edit form if you permit editing */}
                {variant === "picker" && mode === "existing" && (
                  <Button
                    variant="outline-primary"
                    onClick={handlePick}
                    disabled={!selectedCase}
                  >
                    Use This Case
                  </Button>
                )}

                {canEdit && (
                  <Button
                    variant="primary"
                    onClick={handleSave}
                    disabled={!patientId || !icd101}
                  >
                    {mode === "new" ? "Create Case" : "Save Changes"}
                  </Button>
                )}
              </div>
            </div>
          )}
        </Col>
      </Row>

      <ICD10PickerModal
        show={showICDModal}
        onHide={() => setShowICDModal(false)}
        initialSelected={icdSelections}
        onConfirm={(sel) => {
          setIcdSelections(
            sel.map((s) => ({ item: s.item ?? s, isPrimary: !!s.isPrimary }))
          );
          setShowICDModal(false);
        }}
      />
    </div>
  );
}
